import React, { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { LayoutDashboard, Users, Send, Bell, BarChart3, FolderKanban, Settings, LogOut, CheckCircle2, Search, ChevronDown, ChevronRight, FileSpreadsheet, FolderOpen, Zap, ShieldCheck, FileX2 } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "sidebar-nav-dashboard", end: true },
  { to: "/mandantes", label: "Configuración Mandante", icon: FolderKanban, testid: "sidebar-nav-mandantes" },
  { to: "/conversor", label: "Conversor BHP", icon: FileSpreadsheet, testid: "sidebar-nav-conversor" },
  { to: "/trabajadores", label: "Trabajadores", icon: Users, testid: "sidebar-nav-trabajadores" },
  { to: "/acreditacion", label: "Acreditación", icon: FolderOpen, testid: "sidebar-nav-acreditacion" },
  {
    label: "Acciones", icon: Zap, testid: "sidebar-nav-acciones",
    children: [
      { to: "/seguimiento", label: "Seguimiento", icon: Send, testid: "sidebar-nav-seguimiento" },
      { to: "/recordatorios", label: "Recordatorios", icon: Bell, testid: "sidebar-nav-recordatorios" },
    ],
  },
  { to: "/reportes", label: "Reportes", icon: BarChart3, testid: "sidebar-nav-reportes" },
  { to: "/auditoria", label: "Auditoría", icon: ShieldCheck, testid: "sidebar-nav-auditoria" },
  { to: "/finiquitos", label: "Finiquitos", icon: FileX2, testid: "sidebar-nav-finiquitos" },
  { to: "/configuracion", label: "Configuración", icon: Settings, testid: "sidebar-nav-configuracion" },
];

function Sidebar() {
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const canSeeAudit = ["ADMIN", "HR_MANAGER"].includes(user?.role);
  const NAV_FILTERED = NAV.filter((n) => n.to !== "/auditoria" || canSeeAudit);
  const [openGroups, setOpenGroups] = useState(() => {
    const init = {};
    NAV_FILTERED.filter((n) => n.children).forEach((g) => {
      init[g.label] = g.children.some((c) => pathname.startsWith(c.to));
    });
    return init;
  });
  const toggle = (label) => setOpenGroups((p) => ({ ...p, [label]: !p[label] }));

  return (
    <aside className="fixed left-0 top-0 bottom-0 w-64 bg-[#0B132B] text-slate-300 flex flex-col justify-between z-40">
      <div>
        <div className="h-16 px-3 flex items-center justify-center border-b border-slate-800 overflow-hidden">
          <img src="/logo-sidebar.png" alt="Onboarding de Acreditación"
            className="max-h-14 max-w-full w-auto object-contain" />
        </div>
        <nav className="px-3 py-5 space-y-1">
          {NAV_FILTERED.map((n) => {
            if (n.children) {
              const open = openGroups[n.label];
              const anyActive = n.children.some((c) => pathname.startsWith(c.to));
              return (
                <div key={n.label}>
                  <button
                    data-testid={n.testid}
                    onClick={() => toggle(n.label)}
                    className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      anyActive ? "text-white bg-white/5" : "text-slate-400 hover:text-white hover:bg-white/5"
                    }`}
                  >
                    <span className="flex items-center gap-3"><n.icon size={18} /> {n.label}</span>
                    <ChevronRight size={14} className={`transition-transform ${open ? "rotate-90" : ""}`} />
                  </button>
                  {open && (
                    <div className="mt-1 ml-3 pl-3 border-l border-slate-800 space-y-1">
                      {n.children.map((c) => (
                        <NavLink key={c.to} to={c.to} data-testid={c.testid}
                          className={({ isActive }) =>
                            `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                              isActive ? "bg-[#1E293B] text-white" : "text-slate-400 hover:text-white hover:bg-white/5"
                            }`
                          }>
                          <c.icon size={16} /> {c.label}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            return (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={n.testid}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    isActive ? "bg-[#1E293B] text-white" : "text-slate-400 hover:text-white hover:bg-white/5"
                  }`
                }
              >
                <n.icon size={18} /> {n.label}
              </NavLink>
            );
          })}
        </nav>
      </div>
      <div className="px-3 py-4 border-t border-slate-800">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="sidebar-user-menu" className="w-full flex items-center justify-between gap-2 px-2 py-2 rounded-lg hover:bg-white/5 transition-colors text-left">
              <div className="min-w-0 leading-tight">
                <p className="text-sm font-semibold text-white truncate">{(user?.name || "").replace(/\s*\((Admin|Administrador)\)\s*/i, "").trim()}</p>
                <p className="text-xs text-slate-400 truncate">{user?.role}</p>
              </div>
              <ChevronDown size={16} className="text-slate-400 shrink-0" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="top" align="end" className="w-52 bg-white">
            <DropdownMenuLabel className="text-xs text-slate-500">{user?.email}</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid="logout-button" onClick={logout} className="text-red-600 focus:text-red-700">
              <LogOut size={15} className="mr-2" /> Cerrar sesión
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </aside>
  );
}

function Topbar({ search, setSearch }) {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [orgLogo, setOrgLogo] = useState(null);
  useEffect(() => {
    api.get("/alerts").then((r) => setAlerts(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await api.get("/organization");
        if (!data?.has_logo) { if (!cancelled) setOrgLogo(null); return; }
        const r = await api.get("/organization/logo", { responseType: "blob" });
        if (!cancelled) setOrgLogo(URL.createObjectURL(r.data));
      } catch { if (!cancelled) setOrgLogo(null); }
    };
    load();
    const onUpdate = () => load();
    window.addEventListener("organization-logo-updated", onUpdate);
    return () => { cancelled = true; window.removeEventListener("organization-logo-updated", onUpdate); };
  }, []);
  return (
    <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-end px-8 sticky top-0 z-30 shadow-sm gap-5">
      {orgLogo && (
        <img data-testid="topbar-org-logo" src={orgLogo} alt="Logo empresa"
          className="h-7 max-w-[120px] w-auto object-contain my-auto" />
      )}
      <button data-testid="alerts-bell" onClick={() => navigate("/")} className="relative text-slate-500 hover:text-slate-800 flex items-center">
        <Bell size={20} />
        {alerts.length > 0 && (
          <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[10px] font-bold rounded-full h-4 min-w-4 px-1 flex items-center justify-center">
            {alerts.length}
          </span>
        )}
      </button>
    </header>
  );
}

export default function Layout({ children, search, setSearch }) {
  const year = new Date().getFullYear();
  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar />
      <div className="ml-64 min-h-screen flex flex-col">
        <Topbar search={search} setSearch={setSearch} />
        <main className="p-8 space-y-8 flex-1">{children}</main>
        <footer data-testid="app-footer" className="py-3 text-center text-xs text-slate-400">
          © Dosoft {year}
        </footer>
      </div>
    </div>
  );
}
